import {
  Component,
  EventEmitter,
  Output,
  Input,
  SimpleChanges,
  OnInit,
  OnChanges,
  OnDestroy,
} from '@angular/core';
import { Router } from '@angular/router';
import { FlatTreeControl } from '@angular/cdk/tree';
import { Subject } from 'rxjs';
import { PathElement, NodeEntry, Node } from '@alfresco/js-api';
import { takeUntil } from 'rxjs/operators';
import { TreeBaseNode } from '@alfresco/adf-content-services';

import { CONSTANTS } from '../../../model/custom-adf-model';
import { TreeViewDataSource } from './data/tree-view-datasource';
import { WkTreeViewService } from '../../../services/wk-tree-view.service';
import { FolderOperations } from '../../../../app/types';
import { NODE_SITE_CONTAINER_ASPECT, NODE_SITE_TYPE } from '../../../constants';
import { FolderOperationType } from '../../../enums';

@Component({
  selector: 'aca-tree-view-list',
  templateUrl: './custom-tree-view.component.html',
  styleUrls: ['./custom-tree-view.component.scss'],
  standalone: false,
})
export class WkTreeViewComponent implements OnInit, OnChanges, OnDestroy {
  /** Identifier of the node to display. */
  @Input()
  node: Node;

  /** Emitted when a node in the tree view is clicked. */
  @Output()
  nodeClicked: EventEmitter<NodeEntry> = new EventEmitter();

  /** Emitted when an invalid node id is given. */
  @Output()
  treeError: EventEmitter<any> = new EventEmitter();

  treeControl: FlatTreeControl<TreeBaseNode>;
  dataSource: TreeViewDataSource;
  nodeId: string;
  onDestroy$ = new Subject<void>();
  private parentElements: PathElement[];
  nodeCollapsed = false;

  constructor(
    private readonly treeViewService: WkTreeViewService,
    private readonly router: Router
  ) {
    this.treeControl = new FlatTreeControl<TreeBaseNode>(
      this.getLevel,
      this.isExpandable
    );
    this.dataSource = new TreeViewDataSource(
      this.treeControl,
      this.treeViewService,
      '',
      ''
    );
    this.parentElements = [];
  }

  ngOnInit() {
    this.dataSource.dataChange
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        if (
          this.parentElements.length > 0 &&
          this.treeControl.dataNodes &&
          !this.nodeCollapsed
        ) {
          this.openNodesFromParent(this.parentElements);
        }
      });
    this.treeViewService
      .getFolderOperation()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: this.modifyTree.bind(this),
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    this.nodeId = this.node.id;
    this.nodeCollapsed = false;
    if (
      changes['node'] &&
      changes['node'].currentValue &&
      changes['node'].currentValue !== changes['node'].previousValue
    ) {
      if (this.isRootLibrary(this.node) && this.node.isFolder) {
        this.loadTreeNode(this.nodeId);
      } else {
        this.findTreeNode(this.node);
      }
    } else {
      this.dataSource.data = [];
    }
  }

  onNodeClicked(node: NodeEntry) {
    this.nodeClicked.emit(node);
  }

  getLevel = (node: TreeBaseNode) => node.level;

  isExpandable = (node: TreeBaseNode) => node.expandable;

  hasChild = (_: number, nodeData: TreeBaseNode) => nodeData.expandable;

  onNodeNameClicked(node: NodeEntry) {
    this.router.navigate([CONSTANTS.LIBRARY, node.entry.id]);
  }

  private openNodesFromParent(parentNodes: PathElement[]) {
    let getParentElement = false;
    parentNodes.forEach((parent: PathElement) => {
      if (!getParentElement) {
        const treeNode = this.treeControl.dataNodes.find(
          (t) => t.nodeId === parent.id
        );
        if (treeNode) {
          this.treeControl.expand(treeNode);
          getParentElement = true;
          this.parentElements.splice(
            parentNodes.indexOf(parent),
            parentNodes.length
          );
        }
      }
    });
  }

  private findTreeNode(node: Node) {
    this.parentElements = [...node['path']['elements']];
    this.parentElements.push(node);
    if (this.treeControl.dataNodes) {
      const childTreeNodes = this.childrenIsAlreadyExpanded();
      if (childTreeNodes.length > 0) {
        this.nodeCollapsed = true;
        childTreeNodes.forEach((treeNode) =>
          this.treeControl.collapseDescendants(treeNode)
        );
      } else {
        this.parentElements.reverse();
        const parentElements = [...this.parentElements];
        this.openNodesFromParent(parentElements);
      }
    } else {
      const rootNode = this.parentElements[3];
      if (rootNode) {
        this.loadTreeNode(rootNode.id);
        this.parentElements.reverse();
      } else {
        this.parentElements = [];
      }
    }
  }

  private childrenIsAlreadyExpanded(): TreeBaseNode[] {
    return this.treeControl.dataNodes.filter(
      (treeNode) =>
        treeNode.node.entry.parentId === this.nodeId &&
        this.treeControl.isExpanded(treeNode)
    );
  }

  private loadTreeNode(nodeId: string) {
    this.treeViewService
      .getCustomTreeNodes(nodeId)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (treeNode: TreeBaseNode[]) => {
          this.dataSource.data = treeNode;
        },
        error: (error) => this.treeError.emit(error),
      });
  }

  /**
   * This method will check if current node is root folder of any site or not
   */
  private isRootLibrary(node: Node): boolean {
    return (
      node.aspectNames.includes(NODE_SITE_CONTAINER_ASPECT) ||
      (node.path.elements.length === 3 &&
        node.path.elements[2].nodeType === NODE_SITE_TYPE)
    );
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Modifies the tree in the view based on operation. If Edit is performed it would find
   * the tree node and update the data source to trigger the view update, for other operations
   * we would only toggle the parent node, or load the root node again.
   * @param folderOperation defines the operation performed on a given folder
   */
  private modifyTree(folderOperation: FolderOperations): void {
    if (folderOperation.type === FolderOperationType.EDIT) {
      const { folderId, folder } = folderOperation;
      this.dataSource.data = this.dataSource.data.map(
        (treeNode: TreeBaseNode) => {
          if (treeNode.nodeId === folderId) {
            return { ...treeNode, node: folder, name: folder.entry.name };
          }
          return { ...treeNode };
        }
      );
    } else {
      const treeNode = this.treeControl.dataNodes.find(
        (candidateNode: TreeBaseNode) =>
          candidateNode.nodeId === folderOperation.parentFolderId
      );
      if (treeNode) {
        this.treeControl.collapse(treeNode);
        this.treeControl.expand(treeNode);
      } else {
        // if parent node is not present in the list, that means the folder is created directly inside a site container
        // load the root node and update the data source directly.
        this.loadTreeNode(folderOperation.parentFolderId);
      }
    }
  }
}
